import React, { FC, useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  TextInput,
  FlatList,
  StyleSheet,
  Pressable,
  Animated,
} from "react-native";

import { CaretDown, Check } from "../../../assets/index";
import { capitalizeString } from "../../../utils/functions";

type InputDropdownProps = {
  name: string;
  selections: string[];
  value: string;
  required?: boolean;
  allowSearch?: boolean;
  onSelect?: ((value: string) => void) | null;
  placeholder?: string;
  formatLabel?: (value: string) => string;
  dropdownContainerStyle?: import("react-native").ViewStyle;
};

const InputDropdown: FC<InputDropdownProps> = ({
  name,
  selections,
  value = "None",
  required = false,
  allowSearch = false,
  onSelect = null,
  placeholder = "Select",
  formatLabel = capitalizeString,
  dropdownContainerStyle,
}) => {
  const [search, setSearch] = useState("");
  const [clicked, setClicked] = useState(false);
  const [data, setData] = useState<string[]>(selections);
  const [selectedData, setSelectedData] = useState<string>(value);
  const searchRef = useRef<TextInput>(null);
  const [rotateAnim] = useState(new Animated.Value(0));

  const toggleCaret = () => {
    setClicked(!clicked);
    const rotateTo = clicked ? 0 : 1;
    Animated.timing(rotateAnim, {
      toValue: rotateTo,
      duration: 300,
      useNativeDriver: true,
    }).start();
  };

  const onSearch = (searchValue: string) => {
    if (searchValue.length < search.length) {
      let tempSelections = selections;
      tempSelections = tempSelections.filter((item) => {
        return item.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
      });
      setData(tempSelections);
    } else if (searchValue !== "") {
      let tempSelections = data.filter((item) => {
        return item.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
      });
      setData(tempSelections);
    } else {
      setData(selections);
    }
  };

  useEffect(() => {
    setData(selections);
  }, [selections]);

  useEffect(() => {
    setSelectedData(value);
  }, [value]);

  return (
    <View style={styles.container}>
      {name !== "" && (
        <Text style={styles.name}>
          {name} {required && <Text style={{ color: "red" }}>*</Text>}
        </Text>
      )}
      <View style={styles.triggerContainer}>
        <Pressable
          style={[styles.placeHolder, clicked && styles.placeHolderClicked]}
          onPress={toggleCaret}
        >
          <Text
            style={[
              styles.placeHolderText,
              { color: selectedData ? "#000" : "#C4C6CD" },
            ]}
          >
            {selectedData === "" ? placeholder : formatLabel(selectedData)}
          </Text>
          <Animated.View
            style={{
              transform: [
                {
                  rotate: rotateAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: ["0deg", "180deg"],
                  }),
                },
              ],
            }}
          >
            <CaretDown />
          </Animated.View>
        </Pressable>
        {clicked && (
          <Animated.View
            style={[styles.dropdownContainer, dropdownContainerStyle]}
          >
            {allowSearch && (
              <View style={{ marginBottom: 4 }}>
                <TextInput
                  placeholder="Search ..."
                  value={search}
                  ref={searchRef}
                  onChangeText={(txt) => {
                    setSearch(txt);
                    onSearch(txt);
                  }}
                  style={styles.placeHolderText}
                />
              </View>
            )}
            <FlatList
              data={data}
              keyboardShouldPersistTaps="handled"
              // @ts-ignore comment
              renderItem={({ item, index }) => {
                if (item === "") return;
                if (item === selectedData)
                  return (
                    <Pressable
                      key={index}
                      style={[styles.dropdownItem, styles.selectedDataItem]}
                      onPress={() => {
                        setSelectedData(item);
                        if (onSelect) onSelect(item);
                        toggleCaret();
                        onSearch("");
                        setSearch("");
                      }}
                    >
                      <Text style={styles.placeHolderText}>
                        {formatLabel(item)}
                      </Text>
                      <View style={styles.checkIconContainer}>
                        <Check />
                      </View>
                    </Pressable>
                  );
                return (
                  <Pressable
                    key={index}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setSelectedData(item);
                      if (onSelect) onSelect(item);
                      toggleCaret();
                      onSearch("");
                      setSearch("");
                    }}
                  >
                    <Text style={styles.placeHolderText}>
                      {formatLabel(item)}
                    </Text>
                  </Pressable>
                );
              }}
            />
          </Animated.View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    position: "relative",
  },
  triggerContainer: {
    position: "relative",
    height: 48,
    overflow: "visible",
  },
  name: {
    fontFamily: "Urbanist400",
    fontSize: 18,
    fontStyle: "normal",
    lineHeight: 18 * 1.4,
    paddingVertical: 12,
    color: "#282B33",
  },
  placeHolderText: {
    fontFamily: "Urbanist500",
    lineHeight: 16 * 1.4,
    fontSize: 16,
    letterSpacing: 0.032,
    color: "#868B97",
    width: "100%",
    paddingVertical: 4,
    paddingHorizontal: 2,
    userSelect: "none",
  },
  placeHolder: {
    width: "100%",
    height: 46,
    borderRadius: 10,
    borderColor: "#C4C6CD",
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 16,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  placeHolderClicked: {
    borderWidth: 1,
    borderColor: "#085ccc",
  },
  dropdownContainer: {
    width: "100%",
    height: "auto",
    maxHeight: 200,
    backgroundColor: "#fff",
    borderRadius: 6,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    padding: 4,
    position: "absolute",
    top: 50,
    zIndex: 1000,
    elevation: 8,
    overflow: "hidden",
  },
  dropdownItem: {
    width: "100%",
    minHeight: 44,
    justifyContent: "center",
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 4,
  },
  checkIconContainer: {
    position: "absolute",
    right: 8,
  },
  selectedDataItem: {
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: 4,
    backgroundColor: "#F8F8F8",
  },
});

export default InputDropdown;
